'use client';

import React, { ChangeEvent, CSSProperties, useId, useState } from 'react';
import cn from 'classnames';
import { Button, ButtonProps } from '@/ui-components/atoms/Button/Button';
import { Grid } from '@/ui-components/atoms/Layout/Grid/Grid';
import { Text } from '@/ui-components/atoms/Text/Text';
import IconCheckmarkSm from '@/assets/icons/icon-checkmark-sm.svg';
import styles from './dualOfferComparisonTable.module.scss';
export interface PlanComparisonCard {
    id: string;
    planName: string;
    priceText?: React.ReactNode;
    footerText?: React.ReactNode;
    cta: ButtonProps;
    promotedText?: React.ReactNode;
    displayYouGetPlanText?: boolean;
}

export type PlanComparisonRowValueType = 'included' | 'optional-add-on';

export interface PlanComparisonRowValue {
    type: PlanComparisonRowValueType;
    label?: React.ReactNode;
}

export interface PlanComparisonRow {
    id: string;
    title: React.ReactNode;
    description?: React.ReactNode;
    values: [PlanComparisonRowValue, PlanComparisonRowValue];
}

export interface DualOfferComparisonTableYMMData {
    year: string;
    make: string;
    model: string;
}

export interface DualOfferComparisonTableProps {
    caption: string;
    planCards: [PlanComparisonCard, PlanComparisonCard];
    comparisonRows: PlanComparisonRow[];
    className?: string;
    heading?: React.ReactNode;
    subhead?: React.ReactNode;
    ymmBanner?: React.ReactNode;
    plansColumnLabel?: React.ReactNode;
    pricingRowLabel?: React.ReactNode;
    offerHighlightedTheme?: string;
    flowType?: string;
    flowName?: string;
    textLink?: ButtonProps;
}

function getThemeStyle(color?: string): CSSProperties | undefined {
    return color ? ({ '--offer-highlighted-theme': color } as CSSProperties) : undefined;
}

function renderRowValue(value: PlanComparisonRowValue): React.JSX.Element {
    if (value.type === 'included') {
        return (
            <div className={styles.includedIcon}>
                <IconCheckmarkSm aria-hidden="true" />
                <span className="show-for-sr">Included</span>
            </div>
        );
    }

    return <span className={styles.optionalAddOn}>{value.label ?? 'Optional add-on'}</span>;
}

export function DualOfferComparisonTable({
    caption,
    planCards,
    comparisonRows,
    className,
    heading,
    subhead,
    ymmBanner,
    plansColumnLabel,
    pricingRowLabel,
    offerHighlightedTheme,
    flowType,
    flowName,
    textLink,
}: DualOfferComparisonTableProps): React.JSX.Element {
    const reactId = useId();
    const safeId = reactId.replace(/:/g, '');
    const radioName = `dual-offer-comparison-${safeId}`;

    const [selectedPlanId, setSelectedPlanId] = useState(planCards[0].id);

    const selectedPlan = planCards.find((c) => c.id === selectedPlanId) ?? planCards[0];

    const primaryOfferThemeStyle = getThemeStyle(offerHighlightedTheme);

    const handlePlanChange = (event: ChangeEvent<HTMLInputElement>): void => {
        setSelectedPlanId(event.target.value);
    };

    return (
        <>
            <Grid container className={styles.headerGrid} standardMoleculeSpacing>
                <Grid col={{ sm: 12 }}>{ymmBanner}</Grid>
                {heading && (
                    <Grid col={{ sm: 12, md: 8, lg: 8 }} start={{ md: 3, lg: 3 }}>
                        <Text tag="h1" className={styles.heading}>
                            {heading}
                        </Text>
                    </Grid>
                )}
                {subhead && (
                    <Grid col={{ sm: 12, md: 6, lg: 6 }} start={{ lg: 4, md: 4 }}>
                        <Text tag="div" variant="text-xlarge" className={styles.subhead}>
                            {subhead}
                        </Text>
                    </Grid>
                )}
            </Grid>

            <Grid container>
                <Grid col={{ sm: 12 }}>
                    <div
                        className={cn(styles.dualOfferComparisonTableWrapper, className)}
                        style={primaryOfferThemeStyle}
                        {...(flowType ? { 'data-flowtype': flowType } : {})}
                        {...(flowName ? { 'data-flowname': flowName } : {})}
                    >
                        <table className={styles.dualOfferTable}>
                            <caption className="show-for-sr">{caption}</caption>

                            <thead>
                                <tr>
                                    <th scope="col">
                                        <span>{plansColumnLabel ?? 'Plans'}</span>
                                    </th>

                                    {planCards.map((planCard) => {
                                        const isSelected = planCard.id === selectedPlanId;

                                        return (
                                            <th key={planCard.id} scope="col" id={`${safeId}-col-${planCard.id}`}>
                                                <label
                                                    className={cn(styles.offerSelector, {
                                                        [styles.offerSelectorNotPromoted]: !planCard.promotedText,
                                                    })}
                                                >
                                                    <input
                                                        className={styles.offerRadio}
                                                        type="radio"
                                                        name={radioName}
                                                        value={planCard.id}
                                                        checked={isSelected}
                                                        onChange={handlePlanChange}
                                                        aria-label={planCard.planName}
                                                    />

                                                    {planCard.promotedText ? (
                                                        <span className={styles.selectorEyebrow}>
                                                            {planCard.promotedText}
                                                        </span>
                                                    ) : null}

                                                    <span className={styles.selectorPlanName}>{planCard.planName}</span>
                                                </label>
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>

                            <tbody>
                                <tr className={styles.pricingRow}>
                                    <th scope="row" id={`${safeId}-row-pricing`}>
                                        <div className={styles.rowHeaderTitle}>{pricingRowLabel ?? 'Pricing'}</div>
                                    </th>

                                    {planCards.map((planCard) => (
                                        <td
                                            key={planCard.id}
                                            headers={`${safeId}-col-${planCard.id} ${safeId}-row-pricing`}
                                        >
                                            {planCard.priceText ? (
                                                <div className={styles.priceText}>{planCard.priceText}</div>
                                            ) : null}
                                        </td>
                                    ))}
                                </tr>

                                {comparisonRows.map((row) => (
                                    <tr key={row.id}>
                                        <th scope="row" id={`${safeId}-row-${row.id}`}>
                                            <div className={styles.rowHeaderTitle}>{row.title}</div>

                                            {row.description ? (
                                                <div className={styles.rowHeaderDescription}>{row.description}</div>
                                            ) : null}
                                        </th>

                                        {row.values.map((value, index) => (
                                            <td
                                                key={`${row.id}-${planCards[index].id}`}
                                                headers={`${safeId}-col-${planCards[index].id} ${safeId}-row-${row.id}`}
                                            >
                                                {renderRowValue(value)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div aria-live="polite">
                            {selectedPlan.footerText ? (
                                <div className={styles.priceTextFooter}>
                                    {selectedPlan.displayYouGetPlanText ? (
                                        <span className={styles.youGetPlanText}>
                                            You&apos;re getting {selectedPlan.planName}
                                        </span>
                                    ) : null}
                                    {selectedPlan.footerText}
                                </div>
                            ) : null}
                            <Button
                                {...selectedPlan.cta}
                                className={styles.ctaButton}
                                inheritParams={selectedPlan.cta?.inheritParams}
                            />
                            {textLink?.href && (
                                <Button
                                    {...textLink}
                                    type="button"
                                    variant="tertiary"
                                    tertiaryNoPadding={true}
                                    fitting="fill-container"
                                    className={styles.textLinkBelowCta}
                                    inheritParams={textLink?.inheritParams}
                                />
                            )}
                        </div>
                    </div>
                </Grid>
            </Grid>
        </>
    );
}

export default DualOfferComparisonTable;
